import { useDispatch } from 'react-redux';
import { addCompetition, updateCompetition } from '../store/competitionSlice';

export const useCompetitionImport = (onClose) => {
  const dispatch = useDispatch();

  const handleImport = (importedData, showSnackbar, overwrite = false) => {
    if (!importedData) return false;

    try {
      // Check if this is USAG data (has type but no competition object)
      const isUSAGImport = importedData.type && importedData.type.includes('USAG_SANCTION');
      
      // If it's USAG data, merge it into the current competition
      if (isUSAGImport) {
        
        // Check if this is the combined INDIVIDUALS import
        if (importedData.type === 'JSON.USAG_SANCTION_INDIVIDUALS') {
          // Single atomic update for all individual types
          dispatch(updateCompetition({
            type: 'individuals',
            data: {
              gymnasts: importedData.gymnasts || [],
              coaches: importedData.coaches || [],
              judges: importedData.judges || [],
              individuals: importedData.individuals || [],
              teams: importedData.teams || [],
              statistics: importedData.statistics || {},
              metadata: importedData.metadata || {}
            }
          }));
          
          if (typeof showSnackbar === 'function') {
            const counts = [];
            const athleteCount = importedData.gymnasts?.length || 0;
            const coachCount = importedData.coaches?.length || 0;
            const judgeCount = importedData.judges?.length || 0;
            const staffCount = importedData.individuals?.length || 0;
            
            if (athleteCount > 0) counts.push(`${athleteCount} athlete${athleteCount !== 1 ? 's' : ''}`);
            if (coachCount > 0) counts.push(`${coachCount} coach${coachCount !== 1 ? 'es' : ''}`);
            if (judgeCount > 0) counts.push(`${judgeCount} judge${judgeCount !== 1 ? 's' : ''}`);
            if (staffCount > 0) counts.push(`${staffCount} staff`);
            
            showSnackbar(`Successfully imported ${counts.join(', ')}`);
          }
        } else {
          // Separate imports for ATHLETES, COACHES, or CLUBS files
          // Import athletes if present
          if (importedData.gymnasts && importedData.gymnasts.length > 0) {
            dispatch(updateCompetition({
              type: 'athletes',
              data: {
                gymnasts: importedData.gymnasts,
                statistics: importedData.statistics || {},
                metadata: importedData.metadata || {}
              }
            }));
          }

          // Import clubs/teams if present
          if (importedData.teams && importedData.teams.length > 0) {
            dispatch(updateCompetition({
              type: 'clubs',
              data: {
                teams: importedData.teams,
                statistics: { totalTeams: importedData.teams.length },
                metadata: importedData.metadata || {}
              }
            }));
          }

          // Import coaches if present
          if (importedData.coaches && importedData.coaches.length > 0) {
            dispatch(updateCompetition({
              type: 'coaches',
              data: {
                coaches: importedData.coaches,
                metadata: importedData.metadata || {}
              }
            }));
          }

          // Import judges if present
          if (importedData.judges && importedData.judges.length > 0) {
            dispatch(updateCompetition({
              type: 'judges',
              data: {
                judges: importedData.judges,
                statistics: importedData.statistics || {},
                metadata: importedData.metadata || {}
              }
            }));
          }

          if (typeof showSnackbar === 'function') {
            const counts = [];
            const athleteCount = importedData.gymnasts?.length || 0;
            const coachCount = importedData.coaches?.length || 0;
            const judgeCount = importedData.judges?.length || 0;
            
            if (athleteCount > 0) counts.push(`${athleteCount} athlete${athleteCount !== 1 ? 's' : ''}`);
            if (coachCount > 0) counts.push(`${coachCount} coach${coachCount !== 1 ? 'es' : ''}`);
            if (judgeCount > 0) counts.push(`${judgeCount} judge${judgeCount !== 1 ? 's' : ''}`);
            
            showSnackbar(`Successfully imported ${counts.join(', ')}`);
          }
        }
        
        if (onClose) onClose();
        return true;
      }
      
      // If it's a new competition (not an update), ensure proper structure
      if (!importedData.type && !overwrite) {
        const structuredData = {
          competition: {
            ...importedData.competition,
            id: Date.now().toString(),
            addedAt: new Date().toISOString(),
            startDate: importedData.competition?.startDate || null,
            endDate: importedData.competition?.endDate || null,
          },
          sessions: importedData.sessions || [],
          teams: importedData.teams || [],
          gymnasts: importedData.gymnasts || [],
          levels: importedData.levels || [],
          judges: importedData.judges || [],
          coaches: importedData.coaches || [],
          ageGroups: importedData.ageGroups || [],
          events: importedData.events || [],
          rotations: importedData.rotations || [],
          statistics: importedData.statistics || {
            totalTeams: 0,
            totalGymnasts: 0,
            totalScratchedGymnasts: 0,
            totalJudges: 0,
            totalSessions: 0,
            gymnastsByLevel: {},
            gymnastsByAgeGroup: {}
          }
        };

        // console.log('Structured import data:', structuredData);
        dispatch(addCompetition(structuredData));
      } else if (importedData.type) {
        // Handle child object updates
        dispatch(updateCompetition({
          competition: importedData.data.competition,
          type: importedData.type,
          data: importedData.data
        }));
      } else if (overwrite) {
        dispatch(updateCompetition(importedData));
      }

      // Handle success messages for non-USAG imports
      if (importedData?.type === 'clubs' && typeof showSnackbar === 'function') {
        showSnackbar(`Successfully imported ${importedData?.data?.statistics?.totalClubs || 0} clubs`);
      } else if (importedData?.type === 'athletes' && typeof showSnackbar === 'function') {
        showSnackbar(`Successfully imported ${importedData?.data?.gymnasts?.length || 0} athletes`);
      } else if (!isUSAGImport && typeof showSnackbar === 'function') {
        // Fallback for other import types (competitions, etc.)
        showSnackbar('Import successful');
      }
      
      if (onClose) onClose();
      return true;
    } catch (error) {
      console.error('Import failed:', error);
      if (typeof showSnackbar === 'function') {
        showSnackbar('Import failed: ' + error.message);
      }
      return false;
    }
  };

  return handleImport;
};
